import { memo } from "react";

import {
  getSalesmanPriceTable,
  isSalesmanPriceTableEmpty,
  isSpecialPrice,
} from "../../../redux/productDetailsSelectors";
import { PriceFormat } from "../../../../common/components/formatting/PriceFormat";
import { zero } from "../../../../utils/PriceUtils";
import DataDrivenTable from "../../../../common/components/Table/DataDrivenTable";
import { type SalesmanPriceTableEntry } from "~/ProductDetails/models/productDetailsTypes";
import { isRebateValid } from "~/feature/ProductDescriptor/v1/productDescriptorUtils";
import { useTranslation } from "react-i18next";
import type { ProductDescriptor } from "~/feature/ProductDescriptor/v1/types";

interface Props {
  className?: string;
  product: ProductDescriptor;
  selectedPackageCode?: string;
  selectedPackageIndex?: number;
  quantity: number;
}

const ZERO = zero();

const DistributorPriceTable = (props: Props) => {
  const { product, className, quantity } = props;
  const { t } = useTranslation();
  const maxScaleVisible = window.SCHA.config.maxVisiblePriceScale || 99;

  const isPriceAt15Digits = product.isMultiPrice;

  let selectedPackageCode = props.selectedPackageCode;
  if (!selectedPackageCode && props.selectedPackageIndex !== undefined) {
    selectedPackageCode = product.availability?.packagingInformation[props.selectedPackageIndex]?.packCode;
  }

  if (isPriceAt15Digits && !selectedPackageCode) {
    return null;
  }

  const isTableEmpty = isSalesmanPriceTableEmpty(product, selectedPackageCode!);
  const priceTableData = getSalesmanPriceTable(product, selectedPackageCode!);
  const isSpecial = isSpecialPrice(product, selectedPackageCode!);

  if (isTableEmpty || !priceTableData || priceTableData.length === 1) {
    return null;
  }

  const filteredPriceTableData = priceTableData.filter((_, index) => index < maxScaleVisible);

  const canSeeRebate =
    !isSpecial &&
    isRebateValid({ product: props.product, packCode: selectedPackageCode ?? "", totalQuantity: quantity }) &&
    filteredPriceTableData.filter((row: SalesmanPriceTableEntry) => row.rebate).length == filteredPriceTableData.length;

  const COLUMN_FLEX = canSeeRebate
    ? [
        "0 0 35%", // qty
        "0 0 30%", // csp
        "0 0 35%", // rebate
      ]
    : [
        "0 0 40%", // qty
        "0 0 60%", // csp
      ];

  const dataTableContents = filteredPriceTableData.map((row: SalesmanPriceTableEntry, index: number) => {
    const rebateText = `${row.rebate} %`;
    const qtyText =
      row.maxQty !== undefined && index < maxScaleVisible - 1 ? `${row.minQty} - ${row.maxQty}` : `>= ${row.minQty}`;
    const items = [
      <span key={"qty"}>{qtyText}</span>,
      <>
        <PriceFormat key={"csp"} price={row.csp || ZERO} zeroText={"-"} />
      </>,
    ];
    if (canSeeRebate) {
      items.push(<span key={"rebate"}>{rebateText}</span>);
    }
    return items;
  });

  const headerT = (key: string) => t("addProduct.salesman.priceTable." + key);
  const dataTableHeader = [headerT("quantity"), t("wishlistPage.table.header.netPrice")];
  if (canSeeRebate) {
    dataTableHeader.push(headerT("rebate"));
  }

  return (
    <div className={className} data-test-id={"scale-price-table"}>
      <DataDrivenTable header={dataTableHeader} data={dataTableContents} columnFlex={COLUMN_FLEX} />
    </div>
  );
};

export default memo(DistributorPriceTable);
